import React, { useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, RefreshCw, Package } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const SECTIONS = [
  { code: "ALL", label: "All sections" },
  { code: "194J", label: "194J — Professional / technical (10%)" },
  { code: "194Q", label: "194Q — Purchase of goods (0.1%)" },
  { code: "194C", label: "194C — Contractor / logistics (1–2%)" },
  { code: "194H", label: "194H — Brokerage / commission (5%)" },
];

export default function TdsReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [bkOnly, setBkOnly] = useState(true);
  const [section, setSection] = useState("ALL");
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState("");

  const buildParams = () => {
    const p = { bk_only: bkOnly };
    if (from) p.from = from;
    if (to) p.to = to;
    if (section && section !== "ALL") p.section = section;
    return p;
  };

  const run = async () => {
    setBusy("load");
    try {
      const { data } = await api.get(`/analytics/tds/report`, { params: buildParams() });
      setRep(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed"); }
    finally { setBusy(""); }
  };

  const dl = (path) => {
    const q = new URLSearchParams();
    Object.entries(buildParams()).forEach(([k, v]) => q.append(k, String(v)));
    window.open(`${API_BASE}/analytics/tds/${path}?${q.toString()}`, "_blank");
  };

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">TDS Report</h1>
        <p className="text-sm text-muted-foreground">Form-26AS-compatible export of every invoice attracting TDS. Filter by section to see per-section calculations, invoices and totals.</p>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
        <div><label className="text-xs text-slate-500">From</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="tds-from" /></div>
        <div><label className="text-xs text-slate-500">To</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="tds-to" /></div>
        <div>
          <label className="text-xs text-slate-500">Section</label>
          <select className="h-9 border rounded px-2 text-sm w-72" value={section} onChange={(e) => setSection(e.target.value)} data-testid="tds-section">
            {SECTIONS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </div>
        <label className="text-xs flex items-center gap-2 pb-2"><input type="checkbox" checked={bkOnly} onChange={(e) => setBkOnly(e.target.checked)} data-testid="tds-bk-only" /> BK-teacher only</label>
        <Button size="sm" onClick={run} disabled={busy === "load"} data-testid="tds-run-btn"><RefreshCw size={14} /> Run report</Button>
        <Button size="sm" variant="outline" onClick={() => dl("csv")} data-testid="tds-download-csv"><Download size={14} /> CSV</Button>
        <Button size="sm" variant="outline" onClick={() => dl("bundle")} data-testid="tds-download-zip"><Package size={14} /> Zip (CSV + invoices)</Button>
      </CardContent></Card>

      {rep && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Invoices</div><div className="text-2xl font-bold">{rep.count}</div></CardContent></Card>
            <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Taxable</div><div className="text-2xl font-bold">{inr(rep.taxable_total)}</div></CardContent></Card>
            <Card><CardContent className="p-3"><div className="text-xs text-slate-500">TDS</div><div className="text-2xl font-bold text-emerald-800">{inr(rep.tds_total)}</div></CardContent></Card>
            <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Grand total</div><div className="text-2xl font-bold">{inr(rep.grand_total)}</div></CardContent></Card>
          </div>

          {rep.by_section?.length > 0 && (
            <Card><CardContent className="p-3">
              <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Section-wise breakdown</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {rep.by_section.map((b) => (
                  <div key={b.section} className="rounded border p-2 text-xs bg-slate-50" data-testid={`tds-sec-${b.section}`}>
                    <div className="font-semibold text-primary">{b.section}</div>
                    <div className="mt-1">Invoices: <b>{b.count}</b></div>
                    <div>Taxable: <b>{inr(b.taxable)}</b></div>
                    <div className="text-emerald-800">TDS: <b>{inr(b.tds)}</b></div>
                    <div>Grand: <b>{inr(b.grand)}</b></div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}

          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Deductee</TableHead>
                  <TableHead>Centre UID / Insurance ID</TableHead>
                  <TableHead>Section</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Grand Total</TableHead>
                  <TableHead>Payment Mode</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rep.rows || []).map((r) => (
                  <TableRow key={r.order_id} data-testid={`tds-row-${r.order_id}`}>
                    <TableCell>
                      {r.invoice_pdf ? (
                        <a href={`${API_BASE}/order-mgmt/orders/${r.order_id}/invoice/pdf`} target="_blank" rel="noreferrer" className="text-primary underline text-xs inline-flex items-center gap-1"><FileText size={12} /> {r.invoice_number || "PDF"}</a>
                      ) : (
                        <span className="text-xs text-muted-foreground">{r.invoice_number || r.order_number || r.order_id}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">{r.customer_name}</div>
                      <div className="text-[10px] text-slate-500">{r.phone}</div>
                      {r.membership_id && <div className="text-[10px] font-mono text-slate-400">{r.membership_id}</div>}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{r.bk_centre_uid || "—"}</div>
                      {r.insurance_unique_id && <div className="text-[10px] text-slate-500">Ins: {r.insurance_unique_id}</div>}
                      {r.insurance_status && <div className="text-[10px] text-emerald-700">{r.insurance_status}</div>}
                    </TableCell>
                    <TableCell><Badge variant="outline">{r.tds_section} @ {r.tds_rate}%</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{inr(r.taxable_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-800 font-semibold">{inr(r.tds_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(r.grand_total)}</TableCell>
                    <TableCell className="text-xs">
                      <div className="uppercase">{r.payment_mode || "—"}</div>
                      {r.payment_gateway && <div className="text-[10px] text-slate-500">{r.payment_gateway}</div>}
                      {r.transaction_id && <div className="text-[10px] font-mono text-slate-400">{r.transaction_id}</div>}
                    </TableCell>
                    <TableCell><Badge className={r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{r.payment_status || "—"}</Badge></TableCell>
                    <TableCell className="text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
